import { useState, useRef, useEffect } from "react";
import { CaretDown, FloppyDisk, Trash, X, Bookmark } from "@phosphor-icons/react";

/**
 * Compact multi-select pill button. Shows "Label • N" when items are selected.
 */
export function FilterPill({ label, options, selected, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const has = selected && selected.length > 0;
  const toggle = (v) => {
    if (selected.includes(v)) onChange(selected.filter(x => x !== v));
    else onChange([...selected, v]);
  };
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`text-xs px-3 py-1.5 border flex items-center gap-1 ${has ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-border hover:bg-muted"}`}
        data-testid={testId}
      >
        {label}
        {has && <span className="font-mono-mini">·{selected.length}</span>}
        <CaretDown size={10} weight="bold" />
      </button>
      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 min-w-[200px] max-h-72 overflow-y-auto bg-white border border-border shadow-lg">
          {options.length === 0 && <div className="p-3 text-xs text-muted-foreground">No options</div>}
          {options.map(o => {
            const on = selected.includes(o.value);
            return (
              <label key={o.value} className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted ${on ? "bg-muted/50 font-semibold" : ""}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(o.value)} data-testid={`${testId}-opt-${o.value}`} />
                {o.dot && <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: o.dot }} />}
                {o.label}
              </label>
            );
          })}
          {selected.length > 0 && (
            <button onClick={() => onChange([])} className="w-full text-left px-3 py-1.5 text-xs text-[#002FA7] hover:bg-muted border-t border-border">
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Saved-views menu — pick from saved filter combos, save current, delete.
 */
export function SavedViewsMenu({ views = [], onLoad, onSave, onDelete, canShareViews }) {
  const [open, setOpen] = useState(false);
  const [saveMode, setSaveMode] = useState(false);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSaveMode(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const doSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), shared });
    setName(""); setShared(false); setSaveMode(false); setOpen(false);
  };
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} className="text-xs px-3 py-1.5 border border-border bg-white hover:bg-muted flex items-center gap-1" data-testid="saved-views-menu">
        <Bookmark size={12} weight="bold" /> Views {views.length > 0 && <span className="font-mono-mini">·{views.length}</span>}
        <CaretDown size={10} weight="bold" />
      </button>
      {open && (
        <div className="absolute z-30 top-full mt-1 right-0 min-w-[240px] bg-white border border-border shadow-lg">
          {views.length > 0 ? (
            <div className="max-h-64 overflow-y-auto">
              {views.map(v => (
                <div key={v.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted group" data-testid={`view-row-${v.id}`}>
                  <button onClick={() => { onLoad(v); setOpen(false); }} className="flex-1 text-left" data-testid={`view-load-${v.id}`}>
                    {v.name}
                    {v.shared && <span className="ml-1 text-[10px] text-[#002FA7] font-mono-mini uppercase">shared</span>}
                  </button>
                  <button onClick={() => onDelete(v)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600"><Trash size={12} /></button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-3 text-xs text-muted-foreground">No saved views yet.</div>
          )}
          <div className="border-t border-border">
            {saveMode ? (
              <div className="p-2 space-y-2">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && doSave()}
                  placeholder="Name this view…"
                  className="w-full border border-border px-2 py-1 text-sm"
                  autoFocus
                  data-testid="view-save-name"
                />
                {canShareViews && (
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
                    Share with team
                  </label>
                )}
                <div className="flex gap-2">
                  <button onClick={doSave} disabled={!name.trim()} className="btn-primary text-xs px-3 py-1 flex-1 flex items-center justify-center gap-1" data-testid="view-save-btn">
                    <FloppyDisk size={12} weight="bold" /> Save
                  </button>
                  <button onClick={() => { setSaveMode(false); setName(""); }} className="text-xs border border-border px-2 py-1">
                    <X size={12} />
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setSaveMode(true)} className="w-full text-left px-3 py-1.5 text-xs text-[#002FA7] hover:bg-muted flex items-center gap-1" data-testid="view-save-open">
                <FloppyDisk size={12} weight="bold" /> Save current as view…
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
